import { useState, useContext, useCallback } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
} from "@mui/material";
import { SuccessAlertContext } from "App";
import Surround from "./Surround";
import { WarehouseInfo } from "../types";
import { areaData } from "../constants";

type NewRegisterDialogProps = {
  open: boolean;
  onClose: (value: boolean) => void;
  onRegister: (warehouseInfo: WarehouseInfo) => void;
  filteredType: "近隣倉庫" | "センター倉庫";
  registeredPrefectures: string[]; // 追加された登録済み都道府県のプロパティ
};

/**
 *新規倉庫登録モーダル
 */
export default function NewRegisterDialog({
  open,
  onClose,
  onRegister,
  filteredType,
  registeredPrefectures,
}: NewRegisterDialogProps) {
  const { showAlert } = useContext(SuccessAlertContext);
  const [warehouseName, setWarehouseName] = useState<string>("");
  const [selectedAreas, setSelectedAreas] = useState<{ [key: string]: boolean }>({});

  const handleCloseDialog = () => {
    setSelectedAreas({});
    setWarehouseName("");
    onClose(false);
  };

  const handleRegisterWarehouse = () => {
    // 倉庫名が入力されていない場合
    if (!warehouseName.trim()) {
      showAlert("倉庫名を入力して下さい。", "error");
      return;
    }

    // 選択された都道府県のチェック
    const selectedPrefectures = Object.values(selectedAreas).filter((isSelected) => isSelected);
    if (selectedPrefectures.length === 0) {
      showAlert("都道府県を最低1つは選択してください。", "error");
      return;
    }

    const prefInfos = Object.entries(selectedAreas)
      .filter(([_, isSelected]) => isSelected)
      .flatMap(([prefName]) =>
        areaData.flatMap((area) =>
          area.prefectures
            .filter((p) => p.pref_name === prefName)
            .map((pref) => ({
              pref_cd: pref.pref_cd,
              pref_name: pref.pref_name,
            })),
        ),
      );

    const newWarehouse = {
      depot_cd: String(Date.now()),
      depot_name: warehouseName,
      depot_kind: filteredType === "近隣倉庫" ? 1 : 0,
      pref_infos: prefInfos,
    };

    onRegister(newWarehouse);
    handleCloseDialog();
  };

  const toggleAreaSelection = useCallback((area: string, checked: boolean) => {
    setSelectedAreas((prev) => {
      const newSelections = { ...prev };
      areaData
        .find((data) => data.area === area)
        ?.prefectures.forEach((pref) => {
          newSelections[pref.pref_name] = checked;
        });
      return newSelections;
    });
  }, []);

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
      <Box p={2}>
        <DialogTitle>
          {filteredType === "近隣倉庫" ? "近隣倉庫登録" : "センター倉庫登録"}
        </DialogTitle>
        <DialogContent sx={{ overflow: "visible" }}>
          <Box m={1} width="100%" display="flex" justifyContent="center" alignItems="center">
            <Typography variant="body1" sx={{ minWidth: "100px" }}>
              倉庫名：
            </Typography>
            <TextField
              type="text"
              label="倉庫名"
              size="medium"
              value={warehouseName}
              onChange={(e) => setWarehouseName(e.target.value)}
              inputProps={{ maxLength: 100 }}
            />
          </Box>

          {/* チェックボックス */}
          <Box mt={2} width="100%">
            <Surround title="配送範囲">
              <Box p={1.5}>
                {areaData.map((area) => (
                  <Box mt={2} key={area.area}>
                    <Surround title={area.area}>
                      <Box mt={2}>
                        <Box
                          position="absolute"
                          top={-15}
                          left={8}
                          px={1}
                          color="#8B4513"
                          sx={{ backgroundColor: "white" }}
                        >
                          {(() => {
                            // エリアの全都道府県が登録済みであるか確認
                            const allRegistered = area.prefectures.every((pref) =>
                              registeredPrefectures.includes(pref.pref_cd),
                            );

                            // エリアのすべての都道府県が選択されているか確認
                            const allSelected = area.prefectures.every(
                              (pref) =>
                                selectedAreas[pref.pref_name] ||
                                registeredPrefectures.includes(pref.pref_cd),
                            );

                            // ラベルの設定
                            let label;
                            if (allRegistered) {
                              label = `${area.area}選択済`;
                            } else if (allSelected) {
                              label = `${area.area}全解除`;
                            } else {
                              label = `${area.area}全選択`;
                            }

                            return (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    color="secondary"
                                    checked={allSelected}
                                    onChange={(e) => toggleAreaSelection(area.area, !allSelected)}
                                    disabled={allRegistered}
                                    sx={{ opacity: allRegistered ? 0.5 : 1 }}
                                  />
                                }
                                label={<span>{label}</span>}
                              />
                            );
                          })()}
                        </Box>
                        <Box px={2} py={1}>
                          {area.prefectures.map((prefecture) => {
                            const isRegistered = registeredPrefectures.includes(prefecture.pref_cd);
                            return (
                              <FormControlLabel
                                key={prefecture.pref_cd}
                                control={
                                  <Checkbox
                                    color="secondary"
                                    sx={{ opacity: isRegistered ? 0.5 : 1 }}
                                    checked={isRegistered || !!selectedAreas[prefecture.pref_name]}
                                    onChange={(e) => {
                                      setSelectedAreas((prev) => ({
                                        ...prev,
                                        [prefecture.pref_name]: e.target.checked,
                                      }));
                                    }}
                                    disabled={isRegistered}
                                  />
                                }
                                label={prefecture.pref_name}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </Surround>
                  </Box>
                ))}
              </Box>
            </Surround>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="custom-outlined" onClick={handleCloseDialog}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={handleRegisterWarehouse}>登録</Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
